// Procedural WebAudio SFX + ambience (M5). Render-side only — the sim never
// hears audio. Zero assets: every sound is synthesized (oscillators + filtered
// noise), so the payload cost is this file and licensing is a non-issue
// (DESIGN.md §15.1 planned CC0 packs; procedural chosen for v1 — swap-in later).
//
// The context is created lazily on the first user gesture (browser autoplay
// policy — DESIGN.md §13). All methods are safe no-ops before that.

import { Pcg32 } from './shared/rng';

const VOLUME_KEY = 'portage.volume';
const MUTED_KEY = 'portage.muted';

export class GameAudio {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private ambient: GainNode | null = null;
  private noiseBuf: AudioBuffer | null = null;
  private shotBudget = 0;
  private shotBudgetAt = 0;
  private lastPingAt = 0;
  /** audio-only randomness (noise texture, pitch jitter) — lint bans Math.random */
  private rng = new Pcg32(0xa0d10, 7);

  volume: number;
  muted: boolean;

  /** uniform float in [0, 1) for cosmetic variation */
  private rand(): number {
    return this.rng.nextU32() / 0x100000000;
  }

  constructor() {
    const v = Number.parseFloat(localStorage.getItem(VOLUME_KEY) ?? '');
    this.volume = Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0.5;
    this.muted = localStorage.getItem(MUTED_KEY) === '1';
  }

  /** create the context; must be called from a user-gesture handler */
  ensure(): void {
    if (this.ctx) {
      // some browsers suspend an existing context until the next gesture
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return;
    }
    type AudioCtxCtor = new () => AudioContext;
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: AudioCtxCtor }).webkitAudioContext;
    if (!Ctor) return; // no WebAudio: stay silent
    this.ctx = new Ctor();
    this.master = this.ctx.createGain();
    this.master.gain.value = this.muted ? 0 : this.volume;
    this.master.connect(this.ctx.destination);

    // 2s brown-noise buffer, reused by every noise-based sound
    const len = this.ctx.sampleRate * 2;
    this.noiseBuf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const data = this.noiseBuf.getChannelData(0);
    let last = 0;
    for (let i = 0; i < len; i++) {
      const white = this.rand() * 2 - 1;
      last = (last + 0.02 * white) / 1.02;
      data[i] = last * 3.5;
    }
    this.startAmbient();
  }

  setVolume(v: number): void {
    this.volume = Math.min(1, Math.max(0, v));
    localStorage.setItem(VOLUME_KEY, String(this.volume));
    if (this.master && !this.muted) this.master.gain.value = this.volume;
  }

  setMuted(m: boolean): void {
    this.muted = m;
    localStorage.setItem(MUTED_KEY, m ? '1' : '0');
    if (this.master) this.master.gain.value = m ? 0 : this.volume;
  }

  // ---- primitives ----

  private tone(
    freq: number,
    dur: number,
    gain: number,
    type: OscillatorType = 'sine',
    glideTo?: number,
    delay = 0,
  ): void {
    if (!this.ctx || !this.master) return;
    const t0 = this.ctx.currentTime + delay;
    const osc = this.ctx.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t0);
    if (glideTo !== undefined) osc.frequency.exponentialRampToValueAtTime(glideTo, t0 + dur);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(gain, t0 + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(g).connect(this.master);
    osc.start(t0);
    osc.stop(t0 + dur + 0.02);
  }

  private noise(dur: number, filterFreq: number, gain: number, delay = 0): void {
    if (!this.ctx || !this.master || !this.noiseBuf) return;
    const t0 = this.ctx.currentTime + delay;
    const src = this.ctx.createBufferSource();
    src.buffer = this.noiseBuf;
    src.loop = true;
    src.playbackRate.value = 0.7 + this.rand() * 0.6;
    const f = this.ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = filterFreq;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(gain, t0 + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    src.connect(f).connect(g).connect(this.master);
    src.start(t0, this.rand() * 2);
    src.stop(t0 + dur + 0.02);
  }

  /** gentle looping wind bed with a slow swell LFO */
  private startAmbient(): void {
    if (!this.ctx || !this.master || !this.noiseBuf) return;
    const src = this.ctx.createBufferSource();
    src.buffer = this.noiseBuf;
    src.loop = true;
    const f = this.ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = 320;
    this.ambient = this.ctx.createGain();
    this.ambient.gain.value = 0.05;
    const lfo = this.ctx.createOscillator();
    lfo.frequency.value = 0.07;
    const lfoGain = this.ctx.createGain();
    lfoGain.gain.value = 0.025;
    lfo.connect(lfoGain).connect(this.ambient.gain);
    src.connect(f).connect(this.ambient).connect(this.master);
    src.start();
    lfo.start();
  }

  // ---- game sounds ----

  /** building placed: earthy thud */
  place(): void {
    this.noise(0.18, 260, 0.5);
    this.tone(95, 0.22, 0.4, 'sine', 55);
  }

  /** road laid: lighter tap */
  road(): void {
    this.noise(0.08, 700, 0.25);
  }

  /** command rejected: descending buzz */
  reject(): void {
    this.tone(220, 0.16, 0.22, 'square', 130);
  }

  /** alert ping: two rising notes (rate-limited: alert storms are a thing) */
  ping(): void {
    const now = performance.now();
    if (now - this.lastPingAt < 1500) return;
    this.lastPingAt = now;
    this.tone(660, 0.1, 0.25, 'triangle');
    this.tone(880, 0.16, 0.25, 'triangle', undefined, 0.11);
  }

  /** combat shots this tick: short snaps, budget-capped per second */
  shots(count: number): void {
    if (!this.ctx) return;
    const now = performance.now();
    if (now - this.shotBudgetAt > 1000) {
      this.shotBudget = 6;
      this.shotBudgetAt = now;
    }
    const n = Math.min(count, this.shotBudget);
    this.shotBudget -= n;
    for (let i = 0; i < n; i++) this.noise(0.05, 2200, 0.16, i * 0.03);
  }

  /** a unit died: low thump */
  death(): void {
    this.tone(130, 0.3, 0.3, 'sine', 50);
  }

  /** end-of-game stings */
  victory(): void {
    const notes = [523, 659, 784, 1047];
    notes.forEach((f, i) => this.tone(f, 0.35, 0.3, 'triangle', undefined, i * 0.14));
  }

  defeat(): void {
    const notes = [392, 330, 262];
    notes.forEach((f, i) => this.tone(f, 0.5, 0.3, 'sine', undefined, i * 0.22));
  }

  /** research completed / monument stage: bright chime */
  chime(): void {
    this.tone(784, 0.25, 0.22, 'triangle');
    this.tone(1175, 0.3, 0.18, 'triangle', undefined, 0.1);
  }
}
